import { EntitiesService } from 'data/services';
import { EntitiesServiceInterface } from 'domain/services';
import { makeRemoteDeleteBanner } from 'main/factories/usecases/entities/banner/delete-banner-entities-factory';
import { makeRemoteUploadBanner } from 'main/factories/usecases/entities/banner/upload-banner-entities-factory';
import { makeRemoteCreateEntities } from 'main/factories/usecases/entities/create-entities-factory';
import { makeRemoteDeleteEntities } from 'main/factories/usecases/entities/delete-entities-factory';
import { makeRemoteGetAllEntitiesByPerson } from 'main/factories/usecases/entities/get-all-entities-by-person-factory';
import { makeRemoteGetAllEntities } from 'main/factories/usecases/entities/get-all-entities-factory';
import { makeRemoteGetByIdEntities } from 'main/factories/usecases/entities/get-by-id-entities-factory';
import { makeRemoteGroupsCount } from 'main/factories/usecases/entities/group-count-factory';
import { makeRemoteOngoingActivitiesByCourse } from 'main/factories/usecases/entities/ongoing-activities-by-course-factory';
import { makeRemoteDeletePhoto } from 'main/factories/usecases/entities/photo/delete-photo-entities-factory';
import { makeRemoteUploadPhoto } from 'main/factories/usecases/entities/photo/upload-photo-entities-factory';
import { makeRemotePositionCountForEntity } from 'main/factories/usecases/entities/position-count-for-entity-factory';
import {
  makeRemoteAddCourseToEntity,
  makeRemoteAddPersonToEntity,
  makeRemoteChangeSemesterOrder,
  makeRemoteCreateSemester,
  makeRemoteDeleteSemester,
  makeRemoteGetAllCoursesFromEntity,
  makeRemoteGetAllNomenclaturesFromEntity,
  makeRemoteGetAllPersonsFromEntity,
  makeRemoteGetAllSemestersFromEntity,
  makeRemoteGetCourseSettingsFromEntity,
  makeRemoteGetEntityTceCount,
  makeRemoteGetEntityTerminationTermCount,
  makeRemoteGetPaginatedOrganizationActiveTces,
  makeRemoteGetPaginatedOrganizationExpiredTces,
  makeRemoteGetPaginatedOrganizationStudentsWithoutTce,
  makeRemoteGetPaginatedOrganizationTcesDrafts,
  makeRemoteGetPaginatedOrganizationTcesSentToIntegrative,
  makeRemoteGetPaginatedOrganizationTcesWaitingGrantorSignature,
  makeRemoteGetPaginatedOrganizationTcesWaitingInstitutionSignature,
  makeRemoteGetPaginatedOrganizationTcesWaitingStudentSignature,
  makeRemoteGetPersonByCPF,
  makeRemoteGetSemesterById,
  makeRemoteUnlinkCourseFromEntity,
  makeRemoteUnlinkPersonFromEntity,
  makeRemoteUpdateCourseFromEntity,
  makeRemoteUpdateNomenclaturesFromEntity,
  makeRemoteUpdateSemester,
} from 'main/factories/usecases/entities/settings';
import {
  makeRemoteCreateFieldAssessments,
  makeRemoteDeleteFieldAssessments,
  makeRemoteGetAllFieldAssessments,
  makeRemoteGetByIdFieldAssessments,
  makeRemoteUpdateFieldAssessments,
} from 'main/factories/usecases/entities/settings/field-assessments';
import { makeRemoteStudentsCount } from 'main/factories/usecases/entities/students-count-factory';
import { makeRemoteStudentsRegisteredForOrganization } from 'main/factories/usecases/entities/students-registered-for-organization-factory';
import { makeRemoteUpdateEntities } from 'main/factories/usecases/entities/update-entities-factory';

export const makeEntitiesService = (): EntitiesServiceInterface => {
  return new EntitiesService({
    getAllEntities: makeRemoteGetAllEntities(),
    addCourseToEntity: makeRemoteAddCourseToEntity(),
    addPersonTOEntity: makeRemoteAddPersonToEntity(),
    changeSemesterOrder: makeRemoteChangeSemesterOrder(),
    createSemester: makeRemoteCreateSemester(),
    deleteSemester: makeRemoteDeleteSemester(),
    getAllCoursesFromEntity: makeRemoteGetAllCoursesFromEntity(),
    getCourseSettingsFromEntity: makeRemoteGetCourseSettingsFromEntity(),
    getAllNomenclaturesFromEntity: makeRemoteGetAllNomenclaturesFromEntity(),
    getAllPersonsFromEntity: makeRemoteGetAllPersonsFromEntity(),
    getAllSemestersFromEntity: makeRemoteGetAllSemestersFromEntity(),
    getPersonByCPF: makeRemoteGetPersonByCPF(),
    getSemesterById: makeRemoteGetSemesterById(),
    unlinkCourseFromEntity: makeRemoteUnlinkCourseFromEntity(),
    unlinkPersonFromEntity: makeRemoteUnlinkPersonFromEntity(),
    updateCourseFromEntity: makeRemoteUpdateCourseFromEntity(),
    updateNomenclatureFromEntity: makeRemoteUpdateNomenclaturesFromEntity(),
    updateSemester: makeRemoteUpdateSemester(),
    deleteEntities: makeRemoteDeleteEntities(),
    updateEntities: makeRemoteUpdateEntities(),
    createEntities: makeRemoteCreateEntities(),
    getByIdEntity: makeRemoteGetByIdEntities(),
    studentsCount: makeRemoteStudentsCount(),
    positionCountForEntity: makeRemotePositionCountForEntity(),
    groupsCount: makeRemoteGroupsCount(),
    ongoingActivitiesByCourse: makeRemoteOngoingActivitiesByCourse(),
    studentsRegisteredForOrganization:
      makeRemoteStudentsRegisteredForOrganization(),
    getAllEntitiesByPerson: makeRemoteGetAllEntitiesByPerson(),
    uploadBanner: makeRemoteUploadBanner(),
    deleteBanner: makeRemoteDeleteBanner(),
    uploadPhoto: makeRemoteUploadPhoto(),
    deletePhoto: makeRemoteDeletePhoto(),
    getEntityTceCount: makeRemoteGetEntityTceCount(),
    getEntityTerminationTermCount: makeRemoteGetEntityTerminationTermCount(),
    getAllFieldAssessments: makeRemoteGetAllFieldAssessments(),
    getByIdFieldAssessments: makeRemoteGetByIdFieldAssessments(),
    updateFieldAssessments: makeRemoteUpdateFieldAssessments(),
    createFieldAssessments: makeRemoteCreateFieldAssessments(),
    deleteFieldAssessments: makeRemoteDeleteFieldAssessments(),
    getPaginatedOrganizationStudentsWithoutTce:
      makeRemoteGetPaginatedOrganizationStudentsWithoutTce(),
    getPaginatedOrganizationTcesDrafts:
      makeRemoteGetPaginatedOrganizationTcesDrafts(),
    getPaginatedOrganizationTcesWaitingStudentSignature:
      makeRemoteGetPaginatedOrganizationTcesWaitingStudentSignature(),
    getPaginatedOrganizationTcesWaitingGrantorSignature:
      makeRemoteGetPaginatedOrganizationTcesWaitingGrantorSignature(),
    getPaginatedOrganizationTcesWaitingInstitutionSignature:
      makeRemoteGetPaginatedOrganizationTcesWaitingInstitutionSignature(),
    getPaginatedOrganizationActiveTces:
      makeRemoteGetPaginatedOrganizationActiveTces(),
    getPaginatedOrganizationExpiredTces:
      makeRemoteGetPaginatedOrganizationExpiredTces(),
    getPaginatedOrganizationTcesSentToIntegrative:
      makeRemoteGetPaginatedOrganizationTcesSentToIntegrative(),
  });
};
